import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import { IconLock, IconPencil, IconPlus, IconTrash } from '@tabler/icons-react';

import type { OidcGroupMappingDto } from '@shared/services/apiService';

import type { AccessControlApi } from '../model/useAccessControlApi';
import type { SsoMappingsApi } from '../model/useSsoMappingsApi';
import { DeleteSsoMappingDialog } from './DeleteSsoMappingDialog';
import { SsoMappingDialog } from './SsoMappingDialog';
import { useStyles } from './styles';

interface SsoMappingsPanelProps {
  api: AccessControlApi;
  sso: SsoMappingsApi;
}

/** Which directory groups give which permission group of this company on SSO sign-in. */
export const SsoMappingsPanel: React.FC<SsoMappingsPanelProps> = ({ api, sso }) => {
  const { t } = useTranslation();
  const { classes } = useStyles();

  const [dialog, setDialog] = useState<{ mapping: OidcGroupMappingDto | null }>();
  const [toDelete, setToDelete] = useState<OidcGroupMappingDto | null>(null);

  if (sso.isLoading) {
    return (
      <Box className={classes.loader}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <>
      <Box className={classes.panelHeader}>
        <Typography variant="body2" color="text.secondary">
          {t('accessControl.ssoMappings.description')}
        </Typography>
        <Button
          variant="contained"
          startIcon={<IconPlus size={18} />}
          onClick={() => setDialog({ mapping: null })}
          className={classes.noWrap}
          data-testid="sso-mapping-create">
          {t('accessControl.ssoMappings.create')}
        </Button>
      </Box>

      <Alert severity="info" className={classes.ssoNotice}>
        {t('accessControl.ssoMappings.appliesOnSignIn')}
      </Alert>

      <TableContainer component={Paper} className={classes.table}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>{t('accessControl.ssoMappings.columns.groupValue')}</TableCell>
              <TableCell>{t('accessControl.ssoMappings.columns.connector')}</TableCell>
              <TableCell>{t('accessControl.ssoMappings.columns.permissionGroup')}</TableCell>
              <TableCell align="right">{t('accessControl.ssoMappings.columns.actions')}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {sso.mappings.map((mapping) => (
              <TableRow key={mapping.id} hover data-testid={`sso-mapping-row-${mapping.id}`}>
                <TableCell className={classes.groupValueCell}>
                  <Typography variant="body2">{mapping.groupValue}</Typography>
                </TableCell>
                <TableCell>{mapping.connectorId}</TableCell>
                <TableCell>
                  <Box className={classes.nameCell}>
                    <Typography variant="body2" fontWeight={600}>
                      {mapping.permissionGroup.name}
                    </Typography>
                    {mapping.permissionGroup.builtIn && (
                      <Chip
                        size="small"
                        variant="outlined"
                        icon={<IconLock size={14} />}
                        label={t('accessControl.builtIn')}
                      />
                    )}
                  </Box>
                </TableCell>
                <TableCell>
                  <Box className={classes.actions}>
                    <Tooltip title={t('accessControl.common.edit')}>
                      <IconButton size="small" onClick={() => setDialog({ mapping })}>
                        <IconPencil size={18} />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title={t('accessControl.common.delete')}>
                      <IconButton size="small" color="error" onClick={() => setToDelete(mapping)}>
                        <IconTrash size={18} />
                      </IconButton>
                    </Tooltip>
                  </Box>
                </TableCell>
              </TableRow>
            ))}
            {sso.mappings.length === 0 && (
              <TableRow>
                <TableCell colSpan={4} className={classes.emptyState}>
                  <Typography variant="body2" gutterBottom>
                    {t('accessControl.ssoMappings.empty.title')}
                  </Typography>
                  <Typography variant="caption">
                    {t('accessControl.ssoMappings.empty.hint')}
                  </Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <SsoMappingDialog
        open={Boolean(dialog)}
        mapping={dialog?.mapping ?? null}
        api={api}
        sso={sso}
        onClose={() => setDialog(undefined)}
      />
      <DeleteSsoMappingDialog mapping={toDelete} sso={sso} onClose={() => setToDelete(null)} />
    </>
  );
};
